import { useCallback, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Brain } from 'lucide-react'
import { testsApi } from '@/api/tests'
import { learningApi } from '@/api/learning'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { SessionProgress } from '@/components/review/SessionProgress'
import { MultipleChoiceQuestion } from '@/components/tests/MultipleChoiceQuestion'
import { StepNarration } from '@/components/learning/MomentumOpeningCard'
import type { GeneratedTest, TestQuestion } from '@/types/test'

interface SessionStepChallengeProps {
  sessionId: string
  stepIndex: number
  questionCount?: number
  narration?: string | null
  onComplete: () => void
}

export function SessionStepChallenge({
  sessionId,
  stepIndex,
  questionCount = 5,
  narration,
  onComplete,
}: SessionStepChallengeProps) {
  const [test, setTest] = useState<GeneratedTest | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [startedAt] = useState(Date.now())
  const [phase, setPhase] = useState<'idle' | 'loading' | 'active' | 'submitting'>('idle')

  const generateMutation = useMutation({
    mutationFn: () =>
      testsApi.generateTest({
        testType: 'VOCABULARY',
        questionCount,
        questionTypes: ['MULTIPLE_CHOICE'],
      }),
    onSuccess: (data) => {
      setTest(data)
      setPhase('active')
    },
  })

  const completeStepMutation = useMutation({
    mutationFn: (payload: { cardsReviewed: number; correctCount: number; durationMs: number }) =>
      learningApi.completeStep(sessionId, stepIndex, payload),
    onSuccess: () => onComplete(),
  })

  const submitMutation = useMutation({
    mutationFn: (payload: {
      answers: Array<{ questionId: string; answer: string; answerTimeMs: number }>
      durationMs: number
    }) => testsApi.submitTest(test!.id, payload),
    onSuccess: (result) => {
      completeStepMutation.mutate({
        cardsReviewed: result.totalQuestions,
        correctCount: result.correctAnswers,
        durationMs: Date.now() - startedAt,
      })
    },
  })

  const currentQuestion: TestQuestion | undefined = test?.questions[currentIndex]

  const handleStart = () => {
    setPhase('loading')
    generateMutation.mutate()
  }

  const handleAnswer = useCallback((questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }, [])

  const handleNext = () => {
    if (!currentQuestion || !answers[currentQuestion.id]) return

    if (currentIndex >= (test?.questions.length ?? 0) - 1) {
      setPhase('submitting')
      submitMutation.mutate({
        answers: (test?.questions ?? []).map((q) => ({
          questionId: q.id,
          answer: answers[q.id] ?? '',
          answerTimeMs: 5000,
        })),
        durationMs: Date.now() - startedAt,
      })
      return
    }

    setCurrentIndex((value) => value + 1)
  }

  if (phase === 'idle') {
    return (
      <Card className="p-6" data-testid="learning-step-challenge">
        <div className="mb-4 flex items-center gap-2 text-primary">
          <Brain className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Quick challenge</h2>
        </div>
        {narration ? <StepNarration message={narration} /> : null}
        <p className="mb-6 text-sm text-muted-foreground">
          Answer {questionCount} quick questions from your vocabulary — about 2 min.
        </p>
        <Button
          className="min-h-12 w-full"
          data-testid="learning-start-challenge"
          onClick={handleStart}
        >
          Start challenge
        </Button>
      </Card>
    )
  }

  if (phase === 'loading' || !test) {
    return (
      <div className="flex h-48 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (phase === 'submitting' || completeStepMutation.isPending) {
    return (
      <div className="flex h-48 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="mx-auto min-w-0 max-w-2xl space-y-6 overflow-x-hidden" data-testid="learning-step-challenge-active">
      <SessionProgress done={currentIndex} total={test.questions.length} />
      {currentQuestion?.type === 'MULTIPLE_CHOICE' ? (
        <MultipleChoiceQuestion
          questionText={currentQuestion.questionText}
          options={currentQuestion.options ?? []}
          selected={answers[currentQuestion.id]}
          onSelect={(value) => handleAnswer(currentQuestion.id, value)}
        />
      ) : null}
      <Button
        className="min-h-12 w-full"
        disabled={!currentQuestion || !answers[currentQuestion.id]}
        onClick={handleNext}
        data-testid="learning-challenge-next"
      >
        {currentIndex >= test.questions.length - 1 ? 'Finish challenge' : 'Next question'}
      </Button>
    </div>
  )
}
